import { useEffect, useState } from "react";
import { getLockers } from "../../api/axios";
import { useTranslation } from "react-i18next";

const LockersMonitoring = () => {
    const { t } = useTranslation('global');
    const [lockerGroups, setLockerGroups] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Define status colors and labels
    const statusConfig = { 0: { color: '#28a745', label: t("lockers.available") }, 3: { color: '#6c757d', label: t("lockers.service") }, 1: { color: '#dc3545', label: t("lockers.inUse") }, 2: { color: '#17a2b8', label: t("lockers.exceeding") } };

    const fetchLockers = async () => {
        try {
            const response = await getLockers();

            // Group lockers by locker_group
            const groupedLockers = Object.entries(
                response.reduce((acc, locker) => {
                    const group = locker.locker_group || 'Ungrouped';
                    if (!acc[group]) acc[group] = [];
                    acc[group].push(locker);
                    return acc;
                }, {})
            ).map(([groupName, lockers]) => ({
                groupName,
                lockers: lockers.map(locker => ({
                    ...locker,
                    id: locker.id || Date.now() + Math.random(),
                    locker_serial: locker.locker_serial || '',
                    status: locker.locker_status || 0,
                    position_x: locker.position_x || 0,
                    position_y: locker.position_y || 0
                }))
            }));

            setLockerGroups(groupedLockers);
        } catch (err) {
            setError('Failed to fetch lockers');
            console.error('Error fetching lockers:', err);
        }
    };

    const refreshData = () => {
        fetchLockers();
    };

    useEffect(() => {
        fetchLockers();
        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchLockers, 1000);
        return () => clearInterval(interval);
    }, []);

    if (error) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
                gap: '16px'
            }}>
                <div style={{ fontSize: '18px', color: '#dc3545' }}>{error}</div>
                <button
                    onClick={refreshData}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                    }}
                >
                    {t("lockers.tryAgain")}
                </button>
            </div>
        );
    }

    if (lockerGroups.length === 0) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
                fontSize: '18px',
                color: '#6c757d'
            }}>
                {t("lockers.noGroupsFound")}
            </div>
        );
    }

    return (
        <div style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            minHeight: '100vh'
        }}>
            {/* Header with Legend */}
            <div style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                marginBottom: '24px',
                position: 'sticky',
                top: '20px',
                zIndex: 100
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: '24px',
                        color: '#212529',
                        fontWeight: '600'
                    }}>
                        Locker Monitoring
                    </h2>

                    {/* Status Legend */}
                    <div style={{
                        display: 'flex',
                        gap: '16px',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <span style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#495057'
                        }}>
                            Status:
                        </span>
                        {Object.entries(statusConfig).map(([status, config]) => (
                            <div key={status} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: config.color,
                                    borderRadius: '3px',
                                    border: '1px solid rgba(0,0,0,0.1)'
                                }}></div>
                                <span style={{
                                    fontSize: '13px',
                                    color: '#495057'
                                }}>
                                    {config.label}
                                </span>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={refreshData}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Locker Groups */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px'
            }}>
                {lockerGroups.map(group => (
                    <div key={group.groupName} style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                    }}>
                        {/* Group Header */}
                        <div style={{
                            padding: '16px 20px',
                            backgroundColor: '#e9ecef',
                            borderBottom: '2px solid #dee2e6'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: '#212529'
                                }}>
                                    {group.groupName}
                                </h3>
                                <span style={{
                                    fontSize: '14px',
                                    color: '#6c757d',
                                    fontWeight: '500'
                                }}>
                                    {group.lockers.length} {t("lockers.lockers")}
                                </span>
                            </div>
                        </div>

                        {/* Lockers Grid */}
                        <div style={{
                            padding: '20px',
                            position: 'relative',
                            width: '100%',
                            overflow: 'visible'
                        }}>
                            <div style={{
                                position: 'relative',
                                width: '100%',
                                height: (() => {
                                    const cellSize = 80;
                                    const gap = 8;
                                    const maxY = Math.max(...group.lockers.map(l => l.position_y || 0));
                                    return `${(maxY + 1) * (cellSize + gap)}px`;
                                })()
                            }}>
                                {group.lockers.map(locker => {
                                    const cellSize = 80;
                                    const gap = 8;
                                    const left = (locker.position_x || 0) * (cellSize + gap);
                                    const top = (locker.position_y || 0) * (cellSize + gap);

                                    return (
                                        <div
                                            key={locker.id}
                                            style={{
                                                position: 'absolute',
                                                left: `${left}px`,
                                                top: `${top}px`,
                                                width: `${cellSize}px`,
                                                height: `${cellSize}px`,
                                                backgroundColor: statusConfig[locker.status]?.color || statusConfig[0].color,
                                                color: 'white',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '4px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                cursor: 'default',
                                                border: '2px solid rgba(255,255,255,0.3)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '18px',
                                                fontWeight: '700'
                                            }}>
                                                {locker.locker_serial}
                                            </div>
                                            <div style={{
                                                fontSize: '11px',
                                                opacity: 0.9,
                                                fontWeight: '500'
                                            }}>
                                                {statusConfig[locker.status]?.label || 'Unknown'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default LockersMonitoring;